import React, { useRef, useEffect } from 'react';
import CurrentTimeline from './current-timeline';
import { formatDateInTimeZone, formatTimeInTimeZone } from '../../utils/timezone';
import { t } from 'i18next';
import {useCalendarViewModel} from "../../view-model/professional/calendar-view-model";
import {observer} from "mobx-react-lite";
import {CalendarDisplayEvent} from "../../types/calendar-types";
import { Edit3 } from 'lucide-react';


const CalendarDayView: React.FC = observer(() => {
  const calendarViewModel = useCalendarViewModel()
  const dayViewRef = useRef<HTMLDivElement>(null);
  
  // Faire défiler jusqu'à l'heure actuelle au chargement
  useEffect(() => {
    if (dayViewRef.current) {
      const now = new Date();
      const currentHour = now.getHours();
      
      // Trouver l'élément d'heure le plus proche
      const hourId = `day-hour-${currentHour}`;
      const hourElement = document.getElementById(hourId);
      
      if (hourElement) {
        // Défiler jusqu'à l'heure actuelle avec un léger décalage pour voir les heures précédentes
        setTimeout(() => {
          hourElement.scrollIntoView({ block: 'center' });
        }, 100);
      }
    }
  }, []);
  
  return (
    <div className="h-full" ref={dayViewRef}>
      {/* En-tête du jour */}
      <div className="hidden md:block bg-gray-50 dark:bg-gray-800 p-4 mb-4 rounded-lg text-center">
        <div className="text-xl font-semibold">
          {formatDateInTimeZone(calendarViewModel.getCurrentDate, 'EEEE d MMMM')}
        </div>
      </div>
      
      {/* Grille du calendrier */}
      <div className="relative pl-12 sm:pl-20 pr-2 sm:pr-4" id="day-calendar-grid">
        {/* Ligne indiquant l'heure actuelle */}
        <CurrentTimeline />
        
        {/* Lignes d'heures */}
        {calendarViewModel.generateTimeSlots.map((timeSlot: string, hourIndex: number) => {
          // N'afficher que les heures pleines pour les lignes
          if (!timeSlot.endsWith(':00')) return null;
          
          const [hour] = timeSlot.split(':').map(Number);
          
          return (
            <div 
              key={hourIndex}
              id={`day-hour-${hour}`}
              className="relative border-b border-gray-200 dark:border-gray-700 h-24 group">
              {/* Indicateur d'heure */}
              <div className="absolute -left-10 sm:-left-16 top-2 text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400">
                {timeSlot}
              </div>
              
              {/* Ligne horizontale */}
              <div className="absolute left-0 right-0 h-full"></div>
            </div>
          );
        })}
        
        {/* Rendez-vous qui s'étendent sur plusieurs heures */}
        {calendarViewModel.getDayAppointments.map((appointment: CalendarDisplayEvent) => {
          // Calculer la position et la hauteur en fonction de l'heure de début et de la durée
          const startHour = parseInt(formatTimeInTimeZone(appointment.start, 'HH'));
          const startMinute = parseInt(formatTimeInTimeZone(appointment.start, 'mm'));
          const durationMinutes = (appointment.end.getTime() - appointment.start.getTime()) / (1000 * 60);
          
          // Position verticale basée sur l'heure de début (chaque heure = 96px de hauteur)
          const topPosition = (startHour * 96) + (startMinute / 60) * 96;
          // Hauteur basée sur la durée (chaque heure = 96px de hauteur)
          const height = (durationMinutes / 60) * 96;
          
          return (
            <div 
              key={appointment.id} 
              className="absolute p-1 sm:p-2 left-12 sm:left-20 right-2 sm:right-4 rounded-lg text-white shadow-sm overflow-hidden cursor-pointer transition-all hover:shadow-md hover:scale-[1.01] z-10"
              style={{
                backgroundColor: appointment.color,
                top: `${topPosition}px`,
                height: `${height}px`,
                minHeight: '30px',
                transition: 'all 0.2s ease'
              }}
              onClick={() => calendarViewModel.openEventDetails(appointment)}
            >
              <div className="flex flex-col h-full">
                <div className="flex justify-between items-center">
                  <span className="text-xs sm:text-sm font-medium">
                    {formatTimeInTimeZone(appointment.start, 'HH:mm')} -
                    {formatTimeInTimeZone(appointment.end, 'HH:mm')}
                  </span>
                  {appointment.isManualBooking && (
                    <Edit3 size={14} className="ml-1 flex-shrink-0" />
                  )}
                </div>
                <div className="text-xs sm:text-sm font-semibold truncate">{appointment.title}</div>
                <div
                  className={`text-xs px-1.5 py-0.5 rounded-full max-w-max ${appointment.status === 'confirmed'
                    ? 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-100'
                    : appointment.status === 'cancelled'
                      ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100'
                      : appointment.status === 'completed'
                        ? 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-100'
                        : ''
                  }`}
                >
                  {appointment.status === 'confirmed' ? t('calendar.confirmed', 'Confirmé') :
                   appointment.status === 'cancelled' ? t('calendar.cancelled', 'Annulé') :
                   appointment.status === 'completed' ? t('calendar.completed', 'Terminé') : ""}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
});

export default CalendarDayView;
